import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, CircularProgress, Alert, Stack,
  AppBar, Toolbar, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { htmlDocsAPI } from '../../services/api';
import HtmlDocViewer from '../../components/common/HtmlDocViewer';

/**
 * Documentos HTML que la oficina ha entregado al tecnico: checklists,
 * presentaciones, fichas de maquina... Solo llegan los asignados y no ocultos,
 * de eso se encarga el backend.
 *
 * El documento abierto es una pantalla completa aparte (`HtmlDocScreen`), que el
 * shell devuelve en lugar de la navegacion, igual que el detalle de una tarea.
 * Superponerlo no vale: la barra superior del shell es un AppBar, y su z-index
 * (1100) tapa cualquier capa que se le ponga encima sin subir por encima de el.
 */
export function HtmlDocScreen({ doc, onBack }) {
  // El gesto de "atras" del movil es el reflejo natural en una presentacion, asi
  // que se le da algo que retroceder: una entrada de historial que se deshace al
  // volver. Sin esto, el gesto se lleva por delante toda la app del tecnico.
  const backRef = useRef(onBack);
  backRef.current = onBack;

  useEffect(() => {
    window.history.pushState({ gpiHtmlDoc: doc.id }, '');
    const onPop = () => backRef.current();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Si se ha salido con la flecha, la entrada sigue puesta y hay que quitarla;
      // si se ha salido con el gesto, ya se fue sola.
      if (window.history.state?.gpiHtmlDoc === doc.id) window.history.back();
    };
  }, [doc.id]);

  return (
    <Box sx={{
      // 100dvh donde exista: en el movil, 100vh cuenta tambien lo que hay bajo la
      // barra del navegador y recortaria el pie del documento.
      height: '100vh',
      '@supports (height: 100dvh)': { height: '100dvh' },
      display: 'flex', flexDirection: 'column', backgroundColor: '#fff',
    }}>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onBack} aria-label="Volver">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ ml: 1 }}>
            {doc.title}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <HtmlDocViewer docId={doc.id} title={doc.title} sx={{ height: '100%' }} />
      </Box>
    </Box>
  );
}

export default function HtmlDocs({ onOpenDoc }) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await htmlDocsAPI.list();
      setDocs(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido cargar los documentos.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!docs) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (docs.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No tienes documentos asignados.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5, pb: 10 }}>
      <Stack spacing={1.5}>
        {docs.map((doc) => (
          <Card key={doc.id} elevation={2} onClick={() => onOpenDoc(doc)}
            sx={{ cursor: 'pointer' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
                               pb: 2, '&:last-child': { pb: 2 } }}>
              <DescriptionIcon color="primary" />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                  {doc.title}
                </Typography>
                {doc.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {doc.description}
                  </Typography>
                )}
              </Box>
              <ChevronRightIcon color="action" />
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
